// Browser error reporting -> Vitals, our in-house tracker
// (github.com/Ultrahuman-tech/ultrahuman-vitals). Vitals speaks the Sentry
// ingest protocol, so this is the stock @sentry/nextjs SDK with only the
// destination changed. Same setup web-dashboard already runs.
//
// Replaces the New Relic log drain for errors. That drain shipped ~474k log
// LINES/hour (~15.8 GB/day) to a paid vendor to answer "did something break",
// which a stack trace answers better and 30-day retention answers for longer
// than Vercel's 1-day window.
//
// The DSN, shared init options and PII scrub live in lib/vitals-shared.ts —
// see that module for the hardcoded-DSN reasoning and override semantics.
//
// NAMED imports on purpose: `import * as Sentry` pins the whole SDK barrel
// (Replay, Feedback, every export) into the client bundle under Turbopack,
// which cannot tree-shake a namespace import.
;globalThis["_sentryNextJsVersion"] = "16.4.0";import { init } from '@sentry/nextjs';
import {
  resolveDsn,
  baseVitalsOptions,
  scrubEvent,
  isUnhandledNetworkFlake,
  isThirdPartyBrowserNoise,
  shouldReportToVitals,
} from 'lib/vitals-shared';

const dsn = resolveDsn(process.env.NEXT_PUBLIC_VITALS_DSN);

if (!dsn && shouldReportToVitals()) {
  // LOUD, not silent. A missing DSN and a site with no errors look identical
  // from the outside, and that ambiguity is what let a broken pipeline sit
  // unnoticed for a day elsewhere in this migration.
  // eslint-disable-next-line no-console
  console.error(
    '[vitals] reporting disabled via NEXT_PUBLIC_VITALS_DSN="" — browser ' +
      'errors are NOT being reported. Unset it (or fix DEFAULT_DSN in ' +
      'lib/vitals-shared.ts) to re-enable.',
  );
}

if (dsn && shouldReportToVitals()) {
  init({
    ...baseVitalsOptions(),
    dsn,

    // Two default integrations are dropped (Vitals stores errors only, so
    // both are pure cost):
    // - BrowserSession posts a session envelope per page load/navigation for
    //   release health, which Vitals does not implement. (`autoSessionTracking`
    //   was REMOVED in v10; filtering the integration is the v10 expression.)
    // - BrowserTracing patches fetch/XHR/history and installs LCP/CLS/INP
    //   PerformanceObservers — pure overhead with tracing off, synchronously
    //   before hydration on LCP-sensitive marketing pages.
    integrations: (defaults) =>
      defaults.filter(
        (integration) =>
          integration.name !== 'BrowserSession' &&
          integration.name !== 'BrowserTracing',
      ),

    // NOTE: console breadcrumbs are kept but stripped to their label — see
    // trimConsoleBreadcrumb. That is wired in baseVitalsOptions() above, NOT
    // here, because the Node SDK enables consoleIntegration by default too and
    // a client-only wiring left the server shipping raw payloads.

    // Third-party noise that is not our bug. Kept in ignoreErrors (which
    // matches ALL events, handled or not — that is fine for these, nothing
    // first-party ever throws them deliberately).
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /webkit-masked-url:/i,
      // Third-party tags we load but do not own. Their exceptions are theirs.
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
      /connect\.facebook\.net/i,
      /cdn\.segment\.com/i,
      /js\.hs-scripts\.com/i,
      /hubapi\.com/i,
      /pixels\.js/i,
      /unicornStudio\.umd\.js/i,
      // The New Relic browser agent throws its own uncaught TypeErrors
      // (observed on the first preview deploy).
      /js-agent\.newrelic\.com/i,
      /bam\.nr-data\.net/i,
    ],

    // Network-flake suppression lives HERE, not in ignoreErrors, because
    // ignoreErrors drops explicitly captured events too (verified in
    // @sentry/core eventFilters — no mechanism.handled check). This gate
    // drops only UNHANDLED 'Failed to fetch' / 'Load failed' / AbortError
    // noise; any deliberate captureException passes through to the shared
    // PII scrub.
    //
    // KNOWN GAP, do not read this gate as covering it: first-party fetch
    // failures currently reach Vitals through NO path. They are caught and
    // console.error'd at ~50 raw fetch() sites, and the one instrumented
    // wrapper (safeFetch) was moved to legacy/ by main's cleanup with zero
    // importers. Closing that means instrumenting the real call sites, not
    // loosening this filter.
    beforeSend(event) {
      if (isUnhandledNetworkFlake(event)) return null;
      if (isThirdPartyBrowserNoise(event)) return null;
      return scrubEvent(event);
    },
  });
}

// Stale /_next/static chunk after a deploy: one reload, then give up so a
// broken build cannot loop. Recovery must run even when Vitals is off.
if (typeof window !== 'undefined') {
  const CHUNK_RELOAD_KEY = 'uh-chunk-reload';
  const isChunkLoadError = (reason: unknown): boolean => {
    const msg =
      reason instanceof Error
        ? `${reason.name}: ${reason.message}`
        : typeof reason === 'string'
          ? reason
          : '';
    return /ChunkLoadError|Failed to load chunk /i.test(msg);
  };
  const reloadOnce = () => {
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    } catch {
      return;
    }
    window.location.reload();
  };
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadOnce();
  });
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error ?? event.message)) reloadOnce();
  });
}

// NOTE: no onRouterTransitionStart export. It would only feed tracing, which
// is off (and BrowserTracing is filtered out above), so exporting it ships and
// runs a no-op on every client-side navigation. The SDK's missing-hook build
// warning is silenced with suppressOnRouterTransitionStartWarning in
// next.config.js. Turning tracing on later means: re-add the export, stop
// filtering BrowserTracing, and set a tracesSampleRate.
